import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../utils/api';
import { PPEItem, ItemType, AssignableUser } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useOrgConfig } from '../contexts/OrgConfigContext';

const conditionColors: Record<string, string> = { good: '#16a34a', fair: '#ca8a04', poor: '#ea580c', out_of_service: '#dc2626' };
const conditionLabels: Record<string, string> = { good: 'Good', fair: 'Fair', poor: 'Poor', out_of_service: 'Out of Service' };

export default function ItemDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { userCanEdit, userCanRetire, userCanArchive, userCanChangeClassification } = useOrgConfig();
  const canManageGear = user?.role === 'admin' || user?.role === 'quartermaster';

  const [successMessage, setSuccessMessage] = useState('');
  const [item, setItem] = useState<PPEItem | null>(null);
  const [itemTypes, setItemTypes] = useState<ItemType[]>([]);
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<any>({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showRetire, setShowRetire] = useState(false);
  const [retireReason, setRetireReason] = useState('');
  const [createReplacement, setCreateReplacement] = useState(false);
  const [replacementForm, setReplacementForm] = useState<any>({
    manufacturer: '', model: '', size: '', serial_number: '',
    date_manufactured: '', condition: 'good', gear_classification: '',
    station: '', notes: '',
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [dateAnomalyConfirm, setDateAnomalyConfirm] = useState(false);

  useEffect(() => {
    // Reset all transient state when navigating between items
    setSaving(false);
    setEditing(false);
    setShowRetire(false);
    setRetireReason('');
    setCreateReplacement(false);
    setError('');
    setActionLoading('');
    setLoading(true);
    setReplacementForm({
      manufacturer: '', model: '', size: '', serial_number: '',
      date_manufactured: '', condition: 'good', gear_classification: '',
      station: '', notes: '',
    });

    setSuccessMessage('');

    Promise.all([
      api.get<PPEItem>(`/ppe-items/${id}`),
      api.get<ItemType[]>('/item-types'),
      api.get<AssignableUser[]>('/users/assignable'),
    ]).then(([itemData, types, userList]) => {
      setItem(itemData);
      setItemTypes(types);
      setUsers(userList);
      setForm({
        item_type_id: itemData.item_type_id,
        manufacturer: itemData.manufacturer || '',
        model: itemData.model || '',
        size: itemData.size || '',
        serial_number: itemData.serial_number || '',
        date_manufactured: itemData.date_manufactured?.split('T')[0] || '',
        condition: itemData.condition,
        gear_classification: itemData.gear_classification || '',
        assigned_to: itemData.assigned_to || '',
        station: itemData.station || '',
        notes: itemData.notes || '',
      });
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  // Show success banner when arriving via replacement creation
  useEffect(() => {
    const navState = location.state as { replacementCreated?: boolean } | null;
    if (navState?.replacementCreated) {
      setSuccessMessage('Replacement item created successfully. The previous item has been retired.');
      // Clear the navigation state so refreshing doesn't re-show the banner
      navigate(location.pathname, { replace: true, state: {} });
      const timer = setTimeout(() => setSuccessMessage(''), 6000);
      return () => clearTimeout(timer);
    }
  }, [location.state, location.pathname, navigate]);

  const checkDateAnomaly = async (): Promise<boolean> => {
    // Only check if this item is backup and has a manufacture date
    if (form.gear_classification !== 'backup' || !form.date_manufactured) return false;
    // Need an assigned user to compare against
    const assignedTo = form.assigned_to || item?.assigned_to;
    if (!assignedTo) return false;

    try {
      // Fetch all items of same type for same user to find frontline items
      const params = new URLSearchParams();
      params.set('retired', 'false');
      params.set('item_type_id', form.item_type_id || item?.item_type_id || '');
      params.set('gear_classification', 'frontline');
      const items = await api.get<PPEItem[]>(`/ppe-items?${params}`);
      const frontlineForUser = items.filter(i => i.assigned_to === assignedTo && i.id !== id);

      for (const fl of frontlineForUser) {
        if (fl.date_manufactured && form.date_manufactured < fl.date_manufactured.split('T')[0]) {
          return true; // Anomaly: backup is older than frontline
        }
      }
    } catch {
      // If check fails, don't block the save
    }
    return false;
  };

  const handleSave = async (skipAnomalyCheck = false) => {
    setSaving(true);
    setError('');
    try {
      if (!skipAnomalyCheck) {
        const hasAnomaly = await checkDateAnomaly();
        if (hasAnomaly) {
          setSaving(false);
          setDateAnomalyConfirm(true);
          return;
        }
      }
      const updated = await api.put<PPEItem>(`/ppe-items/${id}`, form);
      setItem(updated);
      setEditing(false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRetire = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const body: any = { reason: retireReason };
      if (createReplacement) {
        body.replacement = replacementForm;
      }
      const result = await api.post<any>(`/ppe-items/${id}/retire`, body);
      setShowRetire(false);
      setCreateReplacement(false);
      if (result.replacement_item) {
        navigate(`/inventory/${result.replacement_item.id}`, { state: { replacementCreated: true } });
      } else {
        navigate('/retired');
      }
    } catch (err: any) {
      setError(err.message);
      setSaving(false);
    }
  };

  const handleLifecycleAction = async (action: string) => {
    setActionLoading(action);
    setError('');
    try {
      const updated = await api.post<PPEItem>(`/ppe-items/${id}/${action}`, {});
      setItem(updated);
      // After unretire, navigate back to active inventory
      if (action === 'unretire') navigate(`/inventory/${id}`);
      // After archive, navigate to inventory
      if (action === 'archive') navigate('/inventory');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading('');
    }
  };

  const update = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f: any) => ({ ...f, [field]: e.target.value }));

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading...</div>;
  if (!item) return <div style={{ padding: 40, textAlign: 'center', color: '#dc2626' }}>Item not found</div>;

  const selectedType = itemTypes.find(t => t.id === (editing ? form.item_type_id : item.item_type_id));
  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' as const };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4, color: '#6b7280' };

  function daysUntilExpiry(dateStr: string): number {
    const baseDate = new Date(dateStr);
    const expiry = new Date(baseDate);
    const serviceLife = selectedType?.service_life_years || 10;
    expiry.setFullYear(expiry.getFullYear() + serviceLife);
    return Math.ceil((expiry.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
  }

  return (
    <div>
      <button onClick={() => navigate(-1)}
        style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: 14, marginBottom: 12, padding: 0 }}>
        &larr; Back
      </button>

      {successMessage && (
        <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#166534', fontSize: 14, fontWeight: 500, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>✓ {successMessage}</span>
          <button onClick={() => setSuccessMessage('')} style={{ background: 'none', border: 'none', color: '#166534', cursor: 'pointer', fontSize: 18, padding: 0, lineHeight: 1 }}>&times;</button>
        </div>
      )}

      <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{item.item_type_name}</h1>
              {item.gear_classification && (
                <span style={{
                  background: item.gear_classification === 'frontline' ? '#dbeafe' : '#f3e8ff',
                  color: item.gear_classification === 'frontline' ? '#1d4ed8' : '#7c3aed',
                  padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600, textTransform: 'uppercase'
                }}>
                  {item.gear_classification}
                </span>
              )}
            </div>
            {item.serial_number && <div style={{ fontFamily: 'monospace', color: '#666', fontSize: 14 }}>{item.serial_number}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {item.is_retired ? (
              <>
                <span style={{ background: '#6b7280', color: 'white', padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600 }}>Retired</span>
                {canManageGear && (
                  <button onClick={() => handleLifecycleAction('unretire')}
                    disabled={!!actionLoading}
                    style={{ background: '#eff6ff', color: '#1d4ed8', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500, opacity: actionLoading ? 0.7 : 1 }}>
                    {actionLoading === 'unretire' ? 'Restoring...' : 'Unretire'}
                  </button>
                )}
              </>
            ) : (
              <>
                {!editing ? (
                  <>
                    {userCanEdit && (
                      <button onClick={() => setEditing(true)}
                        style={{ background: '#f3f4f6', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                        Edit
                      </button>
                    )}
                    {userCanRetire && (
                      <button onClick={() => setShowRetire(true)}
                        style={{ background: '#fef2f2', color: '#dc2626', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                        Retire
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button onClick={() => setEditing(false)}
                      style={{ background: '#f3f4f6', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>
                      Cancel
                    </button>
                    <button onClick={() => handleSave()} disabled={saving}
                      style={{ background: '#b91c1c', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{error}</div>}

        {/* NFPA compliance warning */}
        {item.date_manufactured && !item.is_retired && (() => {
          const baseDate = item.date_manufactured;
          if (!baseDate) return null;
          const days = daysUntilExpiry(baseDate);
          const serviceLife = selectedType?.service_life_years || 10;
          if (days <= 365) {
            return (
              <div style={{ background: days <= 0 ? '#fef2f2' : '#fffbeb', border: `1px solid ${days <= 0 ? '#fecaca' : '#fde68a'}`, padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
                <strong>{days <= 0 ? 'NFPA COMPLIANCE: EXPIRED' : 'NFPA Compliance Warning'}</strong>
                <span style={{ marginLeft: 8 }}>
                  {days <= 0 ? `This item has exceeded its ${serviceLife}-year service life.` : `${days} days remaining until ${serviceLife}-year service life limit.`}
                </span>
              </div>
            );
          }
          return null;
        })()}

        {/* Item details grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          {selectedType?.track_manufacturer && (
            <div>
              <label style={labelStyle}>Manufacturer</label>
              {editing ? <input value={form.manufacturer} onChange={update('manufacturer')} style={inputStyle} /> :
                <div style={{ fontSize: 15, fontWeight: 500 }}>{item.manufacturer || '-'}</div>}
            </div>
          )}
          {selectedType?.track_model && (
            <div>
              <label style={labelStyle}>Model</label>
              {editing ? <input value={form.model} onChange={update('model')} style={inputStyle} /> :
                <div style={{ fontSize: 15, fontWeight: 500 }}>{item.model || '-'}</div>}
            </div>
          )}
          {selectedType?.track_size && (
            <div>
              <label style={labelStyle}>Size</label>
              {editing ? <input value={form.size} onChange={update('size')} style={inputStyle} /> :
                <div style={{ fontSize: 15, fontWeight: 500 }}>{item.size || '-'}</div>}
            </div>
          )}
          {selectedType?.track_serial_number && (
            <div>
              <label style={labelStyle}>Serial Number</label>
              {editing ? <input value={form.serial_number} onChange={update('serial_number')} style={inputStyle} /> :
                <div style={{ fontSize: 15, fontWeight: 500, fontFamily: 'monospace' }}>{item.serial_number || '-'}</div>}
            </div>
          )}
          {selectedType?.track_date_manufactured && (
            <div>
              <label style={labelStyle}>Date Manufactured</label>
              {editing ? <input type="date" value={form.date_manufactured} onChange={update('date_manufactured')} style={inputStyle} /> :
                <div style={{ fontSize: 15, fontWeight: 500 }}>{item.date_manufactured ? new Date(item.date_manufactured).toLocaleDateString() : '-'}</div>}
            </div>
          )}
          {selectedType?.track_condition && (
            <div>
              <label style={labelStyle}>Condition</label>
              {editing ? (
                <select value={form.condition} onChange={update('condition')} style={{ ...inputStyle, background: 'white' }}>
                  <option value="good">Good</option><option value="fair">Fair</option>
                  <option value="poor">Poor</option><option value="out_of_service">Out of Service</option>
                </select>
              ) : (
                <span style={{ background: conditionColors[item.condition], color: 'white', padding: '4px 10px', borderRadius: 6, fontSize: 13, fontWeight: 600 }}>
                  {conditionLabels[item.condition]}
                </span>
              )}
            </div>
          )}
          {selectedType?.track_gear_classification && (
            <div>
              <label style={labelStyle}>Classification</label>
              {editing && (canManageGear || userCanChangeClassification) ? (
                <select value={form.gear_classification} onChange={update('gear_classification')} style={{ ...inputStyle, background: 'white' }}>
                  <option value="">Not Set</option>
                  <option value="frontline">Frontline</option>
                  <option value="backup">Backup</option>
                </select>
              ) : (
                <div style={{ fontSize: 15, fontWeight: 500 }}>
                  {item.gear_classification ? (
                    <span style={{
                      background: item.gear_classification === 'frontline' ? '#dbeafe' : '#f3e8ff',
                      color: item.gear_classification === 'frontline' ? '#1d4ed8' : '#7c3aed',
                      padding: '4px 10px', borderRadius: 6, fontSize: 13, fontWeight: 600, textTransform: 'uppercase'
                    }}>
                      {item.gear_classification}
                    </span>
                  ) : '-'}
                </div>
              )}
            </div>
          )}
          {selectedType?.track_assigned_to && (
            <div>
              <label style={labelStyle}>Assigned To</label>
              {editing && canManageGear ? (
                <select value={form.assigned_to} onChange={update('assigned_to')} style={{ ...inputStyle, background: 'white' }}>
                  <option value="">Unassigned</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} {u.badge_number ? `(${u.badge_number})` : ''}</option>)}
                </select>
              ) : (
                <div style={{ fontSize: 15, fontWeight: 500 }}>
                  {item.assigned_first_name ? `${item.assigned_first_name} ${item.assigned_last_name}` : '-'}
                  {item.assigned_badge && <span style={{ color: '#666', marginLeft: 6 }}>({item.assigned_badge})</span>}
                </div>
              )}
            </div>
          )}
          {selectedType?.track_station && (
            <div>
              <label style={labelStyle}>Station</label>
              {editing ? <input value={form.station} onChange={update('station')} style={inputStyle} /> :
                <div style={{ fontSize: 15, fontWeight: 500 }}>{item.station || '-'}</div>}
            </div>
          )}
        </div>

        {selectedType?.track_notes && (
          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Notes</label>
            {editing ? <textarea value={form.notes} onChange={update('notes')} rows={3} style={{ ...inputStyle, resize: 'vertical' }} /> :
              <div style={{ fontSize: 14, color: '#374151' }}>{item.notes || 'No notes'}</div>}
          </div>
        )}

        {/* Quick Actions — lifecycle transitions (permission-aware) */}
        {!item.is_retired && !item.is_archived && !editing && (userCanChangeClassification || userCanArchive) && (
          <div style={{ marginTop: 20, padding: 16, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            <h3 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 600, color: '#374151' }}>Quick Actions</h3>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {userCanChangeClassification && item.gear_classification !== 'frontline' && (
                <button onClick={() => handleLifecycleAction('promote-frontline')}
                  disabled={!!actionLoading}
                  style={{ background: '#dbeafe', color: '#1d4ed8', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500, opacity: actionLoading ? 0.7 : 1 }}>
                  {actionLoading === 'promote-frontline' ? 'Promoting...' : 'Promote to Frontline'}
                </button>
              )}
              {userCanChangeClassification && item.gear_classification !== 'backup' && (
                <button onClick={() => handleLifecycleAction('move-backup')}
                  disabled={!!actionLoading}
                  style={{ background: '#f3e8ff', color: '#7c3aed', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500, opacity: actionLoading ? 0.7 : 1 }}>
                  {actionLoading === 'move-backup' ? 'Moving...' : 'Move to Backup'}
                </button>
              )}
              {userCanArchive && (
                <button onClick={() => handleLifecycleAction('archive')}
                  disabled={!!actionLoading}
                  style={{ background: '#fff7ed', color: '#c2410c', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500, opacity: actionLoading ? 0.7 : 1 }}>
                  {actionLoading === 'archive' ? 'Archiving...' : 'Archive'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Replacement chain */}
        {(item.replaced_by || item.replaces_id) && (
          <div style={{ marginTop: 20, padding: 16, background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 600, color: '#1e40af' }}>Replacement Chain</h3>
            <div style={{ fontSize: 14, color: '#374151' }}>
              {item.replaces_id && (
                <div style={{ marginBottom: 4 }}>
                  Replaces:{' '}
                  <Link to={`/inventory/${item.replaces_id}`}
                    style={{ color: '#1e40af', textDecoration: 'none', fontWeight: 500 }}>
                    {item.replaces_serial || 'Previous item'} &rarr;
                  </Link>
                </div>
              )}
              {item.replaced_by && (
                <div>
                  Replaced by:{' '}
                  <Link to={`/inventory/${item.replaced_by}`}
                    style={{ color: '#1e40af', textDecoration: 'none', fontWeight: 500 }}>
                    {item.replaced_by_serial || 'New item'} &rarr;
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Retirement info */}
        {item.is_retired && (
          <div style={{ marginTop: 20, padding: 16, background: '#f3f4f6', borderRadius: 8 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 600 }}>Retirement Details</h3>
            <div style={{ fontSize: 14, color: '#374151' }}>
              <div>Retired: {item.retired_at ? new Date(item.retired_at).toLocaleDateString() : 'Unknown'}</div>
              {item.retired_by_first_name && <div>By: {item.retired_by_first_name} {item.retired_by_last_name}</div>}
              {item.retirement_reason && <div>Reason: {item.retirement_reason}</div>}
            </div>
          </div>
        )}

        <div style={{ marginTop: 16, fontSize: 12, color: '#9ca3af' }}>
          Created: {new Date(item.created_at).toLocaleString()} | Updated: {new Date(item.updated_at).toLocaleString()}
        </div>
      </div>

      {/* Retire Modal */}
      {showRetire && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}
          onClick={() => setShowRetire(false)}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, maxWidth: 560, width: '100%', maxHeight: '90vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 600 }}>Retire Item</h2>
            <p style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>
              This will move <strong>{item.item_type_name} {item.serial_number}</strong> to the retired inventory.
            </p>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Reason for retirement</label>
              <textarea value={retireReason} onChange={e => setRetireReason(e.target.value)}
                rows={2} style={{ ...inputStyle, resize: 'vertical' }}
                placeholder="e.g. Exceeded 10-year service life, damaged beyond repair..." />
            </div>

            {/* Create Replacement toggle */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                <input type="checkbox" checked={createReplacement} onChange={e => setCreateReplacement(e.target.checked)}
                  style={{ width: 16, height: 16 }} />
                Create replacement item
              </label>
            </div>

            {/* Replacement form (shown when checkbox is checked) */}
            {createReplacement && (
              <div style={{ padding: 16, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb', marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600, color: '#374151' }}>
                  Replacement Details
                </h3>
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
                  The replacement will be assigned to the same user and linked to the retired item.
                  Item type: <strong>{item.item_type_name}</strong>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {selectedType?.track_manufacturer && (
                    <div>
                      <label style={labelStyle}>Manufacturer</label>
                      <input value={replacementForm.manufacturer}
                        onChange={e => setReplacementForm((f: any) => ({ ...f, manufacturer: e.target.value }))}
                        style={inputStyle} />
                    </div>
                  )}
                  {selectedType?.track_model && (
                    <div>
                      <label style={labelStyle}>Model</label>
                      <input value={replacementForm.model}
                        onChange={e => setReplacementForm((f: any) => ({ ...f, model: e.target.value }))}
                        style={inputStyle} />
                    </div>
                  )}
                  {selectedType?.track_size && (
                    <div>
                      <label style={labelStyle}>Size</label>
                      <input value={replacementForm.size}
                        onChange={e => setReplacementForm((f: any) => ({ ...f, size: e.target.value }))}
                        placeholder={item.size || ''}
                        style={inputStyle} />
                    </div>
                  )}
                  {selectedType?.track_serial_number && (
                    <div>
                      <label style={labelStyle}>Serial Number</label>
                      <input value={replacementForm.serial_number}
                        onChange={e => setReplacementForm((f: any) => ({ ...f, serial_number: e.target.value }))}
                        style={inputStyle} />
                    </div>
                  )}
                  {selectedType?.track_date_manufactured && (
                    <div>
                      <label style={labelStyle}>Date Manufactured</label>
                      <input type="date" value={replacementForm.date_manufactured}
                        onChange={e => setReplacementForm((f: any) => ({ ...f, date_manufactured: e.target.value }))}
                        style={inputStyle} />
                    </div>
                  )}
                  {selectedType?.track_condition && (
                    <div>
                      <label style={labelStyle}>Condition</label>
                      <select value={replacementForm.condition}
                        onChange={e => setReplacementForm((f: any) => ({ ...f, condition: e.target.value }))}
                        style={{ ...inputStyle, background: 'white' }}>
                        <option value="good">Good</option>
                        <option value="fair">Fair</option>
                        <option value="poor">Poor</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowRetire(false); setCreateReplacement(false); }}
                style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 }}>
                Cancel
              </button>
              <button onClick={handleRetire} disabled={saving}
                style={{ padding: '10px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                {saving ? 'Processing...' : createReplacement ? 'Retire & Create Replacement' : 'Retire Item'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Date Anomaly Confirmation Dialog */}
      {dateAnomalyConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 16 }}
          onClick={() => setDateAnomalyConfirm(false)}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, maxWidth: 440, width: '100%' }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 600, color: '#92400e' }}>Date Anomaly Detected</h2>
            <p style={{ fontSize: 14, color: '#374151', marginBottom: 16, lineHeight: 1.5 }}>
              This <strong>backup</strong> item has an earlier manufacture date than the corresponding <strong>frontline</strong> item for the same user and type.
            </p>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 20 }}>
              Typically the backup should be newer than the frontline gear. Are you sure you want to save?
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setDateAnomalyConfirm(false)}
                style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 }}>
                Go Back
              </button>
              <button onClick={() => { setDateAnomalyConfirm(false); handleSave(true); }}
                style={{ padding: '10px 20px', background: '#92400e', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                Save Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
